import React, { useEffect, useMemo, useRef } from "react";
import { filter, head, map } from "lodash";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { GlossyThemeProvider } from "@glossy";
import { AbstractDate, DateMath } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { Timezone } from "@linkfluence/models/timezone";
import { useSort } from "@lkf/hooks";
import { Dropdown } from "@radarly/components/dropdown";
import Panel from "@radarly/components/panels/posts";
import Posts from "@radarly/components/posts";
import Post from "@radarly/components/posts/post";
import Toggle from "@radarly/components/toggle";
import {
  useDiscoveryPostlistSummary,
  usePostlistSummary,
} from "@radarly/core/aicha/use-cases";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import { useSearchPosts } from "@radarly/core/posts/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import {
  useCurrentUserLocale,
  useCurrentUserTimezone,
} from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { FocusMetrics, isAichaFacet } from "@radarly/models/aicha";
import { Clause } from "@radarly/models/clause";
import { mergeClauses } from "@radarly/models/clause/merge";
import { refineClauseWithDate } from "@radarly/models/clause/refiner";
import { BaseSearchParams, SearchFragmentPosts } from "@radarly/models/search";
import ScopeAction from "@radarly/shared/cards/actions/scope";
import { PanelPostsSubtitle } from "./subtitle";

type SortOn = SearchFragmentPosts["request"]["pagination"]["sort"]["on"];

export interface IProps {
  title: string;
  clause?: Clause;
  onClose: () => void;
  dateFrom: AbstractDate;
  dateTo?: AbstractDate;
  focusClause?: Clause;
  sortBy?: SortOn;
  timezone?: Timezone;
}

const Centerer = styled.div`
  margin: 0 auto;
  height: 100%;
  max-width: 800px;

  @media (max-width: 850px) {
    margin: 0;
    padding: 0 24px 0 36px;
  }
`;

const StyledDropDown = styled(Dropdown)`
  margin-left: 12px;
`;

const StyledPost = styled(Post)`
  width: 800px;
`;

const Title = styled.div`
  display: inline;
`;

const SORTS = [
  "date",
  Metric.Reach,
  Metric.Engagement,
  "rating",
  "random",
] as const;

function getLabelFromSort(locale: Locale, sortBy: SortOn) {
  switch (sortBy) {
    case Metric.Engagement:
      return tr(locale, "SOCIAL::SHARED::POST::SORTS::ENGAGEMENT");
    /*
    case "radar.impression":
      return tr(locale, "SOCIAL::SHARED::POST::SORTS::IMPRESSION");
      */
    case "rating":
      return tr(locale, "SOCIAL::SHARED::POST::SORTS::RATING");
    case Metric.Reach:
      return tr(locale, "SOCIAL::SHARED::POST::SORTS::REACH");
    case "random":
      return tr(locale, "SOCIAL::SHARED::POST::SORTS::RANDOM");
    default:
      return tr(locale, "SOCIAL::SHARED::POST::SORTS::DATE");
  }
}

const useSummary = (
  pid: number,
  request: BaseSearchParams,
  focusClause: Clause
) => {
  const location = useLocation();
  const isDiscovery = location.pathname.endsWith("/discovery");

  const metrics = map(focusClause?.and, (innerClause) => {
    const [key, value] = head(Object.entries(innerClause));
    const flattenValue = Array.isArray(value) ? head(value) : value;
    return { name: key as Facet, value: flattenValue };
  });
  const whitelistedMetrics = filter(metrics, (metric) =>
    isAichaFacet(metric.name)
  ) as FocusMetrics[];

  const ip = usePostlistSummary(
    pid,
    !isDiscovery && request,
    whitelistedMetrics
  );
  const discovery = useDiscoveryPostlistSummary(
    pid,
    isDiscovery && request,
    whitelistedMetrics
  );

  return isDiscovery ? discovery.summary : ip.summary;
};

const RadarlySharedPanelsPostPanel = ({
  title,
  clause,
  onClose,
  dateFrom,
  dateTo = DateMath.Now,
  focusClause,
  sortBy,
  timezone,
}: IProps) => {
  const isContextualExplainerEnabled = useFeature("contextual_explainer");
  const { sort, setSort, toggleOrder } = useSort<SortOn>(
    sortBy === "impression" || sortBy === "virality" ? "date" : sortBy || "date"
  );
  const userTimezone = useCurrentUserTimezone();
  const tz = timezone || userTimezone;
  const locale = useCurrentUserLocale();
  const project = useCurrentProject();
  const contextualFocuses = useContextualFocuses();
  const fctx = useMemo(() => map(contextualFocuses, "id"), [contextualFocuses]);
  const options = map(SORTS, (_sort) => {
    return {
      id: _sort,
      label: getLabelFromSort(locale, _sort),
    };
  });
  const request: SearchFragmentPosts["request"] = useMemo(() => {
    return {
      query: refineClauseWithDate(mergeClauses(clause, focusClause), {
        from: dateFrom,
        to: dateTo,
      }),
      timezone: tz,
      highlight: true,
      fctx,
      pagination: {
        sort: {
          on: sort.key,
          order: sort.order,
        },
      },
    };
  }, [clause, focusClause, dateFrom, dateTo, tz, fctx, sort.key, sort.order]);

  const { posts, total, isLoading, canFetchMore, isFetchingMore, fetchMore } =
    useSearchPosts(project.id, request);
  const summary = useSummary(
    project.id,
    isContextualExplainerEnabled && request,
    focusClause
  );

  const scrollRef = useRef<HTMLDivElement>();
  useEffect(() => {
    if (scrollRef) {
      scrollRef.current.scroll({ top: 0, behavior: "smooth" });
    }
  }, [sort]);

  return (
    <GlossyThemeProvider name="body">
      <Panel
        ref={scrollRef}
        onClose={onClose}
        options={
          <>
            <Toggle onClick={toggleOrder} />
            <StyledDropDown
              values={options}
              selected={sort.key}
              onSelect={setSort}
            />
          </>
        }
        title={
          <>
            <Title dangerouslySetInnerHTML={{ __html: title }} />
            <ScopeAction clause={clause} />
          </>
        }
        subtitle={
          <PanelPostsSubtitle
            from={dateFrom}
            to={dateTo}
            isLoading={isLoading}
            total={total}
            focusClause={focusClause}
            timezone={tz}
          />
        }
      >
        <Centerer>
          <Posts
            posts={posts}
            summary={summary}
            locale={locale}
            isLoading={isLoading}
            canFetchMore={canFetchMore}
            isFetchingMore={isFetchingMore}
            fetchMore={fetchMore}
            withSummary={isContextualExplainerEnabled}
          >
            {(post) => (
              <GlossyThemeProvider name="surface" key={post.uid}>
                <StyledPost
                  post={post}
                  project={project}
                  locale={locale}
                  timezone={tz}
                  queries={contextualFocuses}
                />
              </GlossyThemeProvider>
            )}
          </Posts>
        </Centerer>
      </Panel>
    </GlossyThemeProvider>
  );
};

export default RadarlySharedPanelsPostPanel;
